import useDiscoveryFetch from './useDiscoveryFetch';
import { useState } from 'react';
import api from '../../api/axiosInstance';
import { useParams } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Card,
    Image,
    Spinner,
    Alert,
} from 'react-bootstrap';
import Sidebar from '../../Sidebar';
import Breadcrumb from '../../Breadcrumb';
import NavTabs from '../../NavTabs';
import { useMobileAiPageContext } from '../../MobileAiAssistantContext';
import { getDiscoveryAiSuggestions } from '../../utils/discoveryAiSuggestions';
import icCity from '../../assets/img/ic-city.svg';
import icGlobe from '../../assets/img/ic-globe.svg';
import icNumber from '../../assets/img/ic-number.svg';
import icPeople from '../../assets/img/ic-user-group.svg';
import icNotes from '../../assets/img/ic-news.svg';
import icFlag from '../../assets/img/ic-banner.svg';
import icLink from '../../assets/img/ic-link-square.svg';
import icHome from '../../assets/img/ic-home.svg';
import icPhone from '../../assets/img/ic-smart-phone.svg';
import { trackEvent } from '../../services/analytics/analyticsService';
import { ANALYTICS_EVENTS } from '../../services/analytics/analyticsEvents';
import { useMediaQuery } from 'react-responsive';
import CityDetailHero from './cityDetail/CityDetailHero';
import CityDetailMobileTabs from './cityDetail/CityDetailMobileTabs';
import useCommunityMembership from '../Communities/useCommunityMembership';
import CommunityCta from '../Communities/CommunityCta';

const DiscoveryCityView = () => {
    const isMobile = useMediaQuery({ query: '(max-width: 991.98px)' });
    const { id, cityId: nestedCityId } = useParams();
    const cityId = nestedCityId || id;
    const countryId = nestedCityId ? id : null;
    const cityBasePath = countryId
        ? `/discovery/countries/${countryId}/cities/${cityId}`
        : `/discovery/cities/${cityId}`;

    // Looked up once here (the City's actual _id, same one the discovery fetch
    // below uses) so rendering the CTA from both the mobile hero and the
    // desktop header never issues two membership lookup requests.
    const communityStatus = useCommunityMembership({ type: 'city', refId: cityId });

    const [cityData, setCityData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useDiscoveryFetch(({ signal, isCurrent, isReconnect }) => {
        const fetchCity = async () => {
            try {
                if (!isReconnect || !cityData) setLoading(true);
                setError('');
                const response = await api.get(`/api/discovery/cities/${cityId}`, { signal });
                if (!isCurrent()) return;
                setCityData(response.data);

                if (!isReconnect) trackEvent(ANALYTICS_EVENTS.DISCOVERY_CITY_VIEWED, {
                    cityId: response.data?._id || cityId,
                    cityName: response.data?.name || response.data?.cityName || '',
                    countryCode: String(response.data?.country?.id || response.data?.country?.iso2 || countryId || '').toUpperCase(),
                    countryName: response.data?.country?.name || '',
                    level: 2,
                    source: countryId ? 'country_city_detail_page' : 'direct_city_detail_page',
                });
            } catch (err) {
                if (!isCurrent()) return;
                console.error('Error fetching city:', err);
                setError('An error occurred while fetching city details.');
            } finally {
                if (!isCurrent()) return;
                setLoading(false);
            }
        };

        fetchCity();
    }, [cityId, countryId]);

    const normalizeUrl = (raw) => {
        const s = (raw || '').trim();
        if (!s) return null;
        const withScheme = /^[a-zA-Z][a-zA-Z0-9+\-.]*:/.test(s) ? s : `https://${s}`;
        try {
            const u = new URL(withScheme);
            if (!['http:', 'https:'].includes(u.protocol)) return null;
            return u.toString();
        } catch {
            return null;
        }
    };

    const formData = {
        name: cityData?.name || '',
        country: cityData?.country || {},
        regionCitizensNumber: cityData?.regionCitizensNumber || '',
        url: cityData?.url || '',
        languagesSpoken: Array.isArray(cityData?.languagesSpoken) ? cityData.languagesSpoken : [],
        cityRegionDescription: cityData?.cityRegionDescription || '',
        universitiesNumber: cityData?.universitiesNumber || '',
        studentsNumber: cityData?.studentsNumber || '',
        officeUrl: cityData?.officeUrl || '',
        address: cityData?.address || '',
        phone: cityData?.phone || '',
        workingHours: Array.isArray(cityData?.workingHours) ? cityData.workingHours : [],
    };

    const safeUrlHref = normalizeUrl(formData.url);
    const safeOfficeHref = normalizeUrl(formData.officeUrl);

    const discoveryCityNavItems = [
        { to: cityBasePath, eventKey: 'about', label: 'General Info', end: true },
        { to: `${cityBasePath}/jobs`, eventKey: 'companies', label: 'Companies & Jobs', alsoActive: [`${cityBasePath}/companies`] },
        { to: `${cityBasePath}/salary`, eventKey: 'salary', label: 'Average salaries' },
        { to: `${cityBasePath}/insights`, eventKey: 'insights', label: 'City insights' },
        { to: `${cityBasePath}/universities`, eventKey: 'universities', label: 'Universities' },
        { to: `${cityBasePath}/comments`, eventKey: 'comments', label: 'Comments' },
    ];

    // Opt this page into the shared mobile AI assistant (header trigger + bottom sheet).
    useMobileAiPageContext({
        pageType: 'discovery-city',
        discoveryType: 'cities',
        cityId,
        cityName: cityData?.name || cityData?.cityName || '',
        countryName: cityData?.country?.name || '',
        activeTab: 'General info',
        suggestions: getDiscoveryAiSuggestions({
            discoveryType: 'cities',
            selectedCity: cityData?.name || cityData?.cityName || '',
            selectedCountry: cityData?.country?.name || '',
        }),
    });

    if (loading) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1">
                    <Container>
                        <Row>
                            <Col className="py-5 text-center">
                                <Spinner animation="border" />
                            </Col>
                        </Row>
                    </Container>
                </main>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1">
                    <Container>
                        <Row>
                            <Col>
                                <Alert variant="danger" className="mt-4">{error}</Alert>
                            </Col>
                        </Row>
                    </Container>
                </main>
            </>
        );
    }

    if (!cityData) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1">
                    <Container>
                        <Row>
                            <Col>
                                <Alert variant="warning" className="mt-4">City not found.</Alert>
                            </Col>
                        </Row>
                    </Container>
                </main>
            </>
        );
    }

    if (isMobile) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1">
                    <Container>
                        <Row>
                            <Col>
                                <Breadcrumb
                                    countryName={countryId ? formData.country?.name : undefined}
                                    subPage={countryId ? 'Cities' : undefined}
                                    cityName={formData.name}
                                />
                            </Col>
                        </Row>

                        <div className="country-detail-mobile city-detail-mobile">
                            <CityDetailHero
                                cityName={formData.name}
                                url={safeUrlHref ? { href: safeUrlHref, label: formData.url } : null}
                                countryName={formData.country?.name}
                                communityAction={<CommunityCta status={communityStatus} />}
                            />
                            <CityDetailMobileTabs items={discoveryCityNavItems} />

                            <Card className="country-detail-content-card mb-4">
                                <Card.Body>
                                    <Card className="h-100 mb-4">
                                        <Card.Body>
                                            <Row><Col><h3><span className="item-number">1.</span> General Info</h3><hr /></Col></Row>
                                            <Row>
                                                {[
                                                    [icCity, 'City Name', formData.name || 'N/A'],
                                                    [icGlobe, 'Country', formData.country.name || 'N/A'],
                                                    [icNumber, 'Region citizen number', formData.regionCitizensNumber || 'N/A'],
                                                    [icLink, 'Regional URL', safeUrlHref ? <a href={safeUrlHref} target="_blank" rel="noopener noreferrer">{formData.url}</a> : 'N/A'],
                                                    [icFlag, 'Local language(s)', formData.languagesSpoken.length ? formData.languagesSpoken.join(', ') : 'N/A'],
                                                    [icCity, 'Number of Universities', formData.universitiesNumber || 'N/A'],
                                                    [icPeople, 'Number of Students', formData.studentsNumber || 'N/A'],
                                                    [icNotes, 'City Region Description', formData.cityRegionDescription || 'N/A'],
                                                ].map(([icon, label, value], index, items) => (
                                                    <Col xs={12} key={label} className={index < items.length - 1 ? 'mb-3' : undefined}>
                                                        <div className="icon-text"><span className="icon" aria-hidden="true"><Image src={icon} alt={label} /></span><span className="text">{label}<br /><strong>{value}</strong></span></div>
                                                    </Col>
                                                ))}
                                            </Row>
                                        </Card.Body>
                                    </Card>

                                    <Card className="h-100">
                                        <Card.Body>
                                            <Row><Col><h3><span className="item-number">2.</span> Citizen office desk</h3><hr /></Col></Row>
                                            <Row>
                                                {[
                                                    [icLink, 'Citizen office desk URL', safeOfficeHref ? <a href={safeOfficeHref} target="_blank" rel="noopener noreferrer">{formData.officeUrl}</a> : 'N/A'],
                                                    [icHome, 'Address', formData.address || 'N/A'],
                                                    [icLink, 'Opening hours', formData.workingHours.length ? formData.workingHours.filter(Boolean).map((hours, index) => <span key={index}>{hours.fromDay || '-'}{hours.toDay ? `–${hours.toDay}` : ''}:&nbsp;{hours.fromTime || '-'}{hours.toTime ? `–${hours.toTime}` : ''}{index < formData.workingHours.length - 1 && <br />}</span>) : 'N/A'],
                                                    [icPhone, 'Phone', formData.phone || 'N/A'],
                                                ].map(([icon, label, value], index, items) => (
                                                    <Col xs={12} key={label} className={index < items.length - 1 ? 'mb-3' : undefined}>
                                                        <div className="icon-text"><span className="icon" aria-hidden="true"><Image src={icon} alt={label} /></span><span className="text">{label}<br /><strong>{value}</strong></span></div>
                                                    </Col>
                                                ))}
                                            </Row>
                                        </Card.Body>
                                    </Card>
                                </Card.Body>
                            </Card>
                        </div>
                    </Container>
                </main>
            </>
        );
    }

    return (
        <>
            <Sidebar />
            <main className="main-content flex-grow-1">
                <Container>
                    <Row>
                        <Col>
                            <Breadcrumb
                                countryName={countryId ? formData.country?.name : undefined}
                                subPage={countryId ? 'Cities' : undefined}
                                cityName={formData.name}
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col>
                            <Card className="mb-4">
                                <Card.Body className="pt-0">
                                    <Row>
                                        <Col xs={12}>
                                            <>
                                                <Row>
                                                    <Col>
                                                        <Card className="card-premium">
                                                            <Card.Body>
                                                                <Row className="align-items-center">
                                                                    <Col>
                                                                        <h3 className="text-start">City</h3>
                                                                    </Col>
                                                                    <Col xs="auto">
                                                                        <CommunityCta status={communityStatus} />
                                                                    </Col>
                                                                </Row>
                                                                <hr />

                                                                <Row className="mb-4">
                                                                    <Col md={8} className="d-flex">
                                                                        <div>
                                                                            <h1>{formData.name || '—'}</h1>
                                                                            <p>
                                                                                {safeUrlHref ? (
                                                                                    <a href={safeUrlHref} target="_blank" rel="noopener noreferrer">
                                                                                        {formData.url}
                                                                                    </a>
                                                                                ) : '—'}
                                                                                {formData.country?.name && (
                                                                                    <>
                                                                                        <span className="dot"></span>
                                                                                        Country: <strong>{formData.country.name}</strong>
                                                                                    </>
                                                                                )}
                                                                            </p>
                                                                        </div>
                                                                    </Col>
                                                                </Row>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                </Row>

                                                <Row className="mb-5">
                                                    <Col>
                                                        <NavTabs items={discoveryCityNavItems} />
                                                    </Col>
                                                </Row>

                                                {/* 1. General Info */}
                                                <Row className="mb-4">
                                                    <Col>
                                                        <Card className="h-100">
                                                            <Card.Body>
                                                                <Row>
                                                                    <Col md={12}>
                                                                        <h3><span className="item-number">1.</span> General Info</h3>
                                                                        <hr />
                                                                    </Col>
                                                                </Row>

                                                                <Row className="mb-4">
                                                                    <Col md={4}>
                                                                        <div className="icon-text">
                                                                            <span className="icon" aria-hidden="true">
                                                                                <Image src={icCity} alt="City Name" />
                                                                            </span>
                                                                            <span className="text">
                                                                                City Name
                                                                                <br />
                                                                                <strong>
                                                                                    {formData.name || 'N/A'}
                                                                                </strong>
                                                                            </span>
                                                                        </div>
                                                                    </Col>
                                                                    <Col md={4}>
                                                                        <div className="icon-text">
                                                                            <span className="icon" aria-hidden="true">
                                                                                <Image src={icGlobe} alt="Country" />
                                                                            </span>
                                                                            <span className="text">
                                                                                Country
                                                                                <br />
                                                                                <strong>
                                                                                    {formData.country.name || 'N/A'}
                                                                                </strong>
                                                                            </span>
                                                                        </div>
                                                                    </Col>
                                                                    <Col md={4}>
                                                                        <div className="icon-text">
                                                                            <span className="icon" aria-hidden="true">
                                                                                <Image src={icNumber} alt="Region citizen number" />
                                                                            </span>
                                                                            <span className="text">
                                                                                Region citizen number
                                                                                <br />
                                                                                <strong>
                                                                                    {formData.regionCitizensNumber || "N/A"}
                                                                                </strong>
                                                                            </span>
                                                                        </div>
                                                                    </Col>
                                                                </Row>
                                                                <Row className="mb-4">
                                                                    <Col md={4}>
                                                                        <div className="icon-text">
                                                                            <span className="icon" aria-hidden="true">
                                                                                <Image src={icLink} alt="Regional URL" />
                                                                            </span>
                                                                            <span className="text">
                                                                                Regional URL
                                                                                <br />
                                                                                <strong>
                                                                                    {safeUrlHref ? (
                                                                                        <a href={safeUrlHref} target="_blank" rel="noopener noreferrer">
                                                                                            {formData.url}
                                                                                        </a>
                                                                                    ) : 'N/A'}
                                                                                </strong>
                                                                            </span>
                                                                        </div>
                                                                    </Col>
                                                                    <Col md={4}>
                                                                        <div className="icon-text">
                                                                            <span className="icon" aria-hidden="true">
                                                                                <Image src={icFlag} alt="Local language(s)" />
                                                                            </span>
                                                                            <span className="text">
                                                                                Local language(s)
                                                                                <br />
                                                                                <strong>
                                                                                    {formData.languagesSpoken.length
                                                                                        ? formData.languagesSpoken.join(', ')
                                                                                        : 'N/A'
                                                                                    }
                                                                                </strong>
                                                                            </span>
                                                                        </div>
                                                                    </Col>
                                                                </Row>
                                                                <Row className="mb-4">
                                                                    <Col md={4}>
                                                                        <div className="icon-text">
                                                                            <span className="icon" aria-hidden="true">
                                                                                <Image src={icCity} alt="Number of Universities" />
                                                                            </span>
                                                                            <span className="text">
                                                                                Number of Universities
                                                                                <br />
                                                                                <strong>
                                                                                    {formData.universitiesNumber || 'N/A'}
                                                                                </strong>
                                                                            </span>
                                                                        </div>
                                                                    </Col>
                                                                    <Col md={4}>
                                                                        <div className="icon-text">
                                                                            <span className="icon" aria-hidden="true">
                                                                                <Image src={icPeople} alt="Number of Students" />
                                                                            </span>
                                                                            <span className="text">
                                                                                Number of Students
                                                                                <br />
                                                                                <strong>
                                                                                    {formData.studentsNumber || 'N/A'}
                                                                                </strong>
                                                                            </span>
                                                                        </div>
                                                                    </Col>
                                                                </Row>
                                                                <Row>
                                                                    <Col md={12}>
                                                                        <div className="icon-text">
                                                                            <span className="icon" aria-hidden="true">
                                                                                <Image src={icNotes} alt="City Region Description" />
                                                                            </span>
                                                                            <span className="text">
                                                                                City Region Description
                                                                                <br />
                                                                                <strong>
                                                                                    {formData.cityRegionDescription || 'N/A'}
                                                                                </strong>
                                                                            </span>
                                                                        </div>
                                                                    </Col>
                                                                </Row>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                </Row>

                                                {/* 2. Citizen office desk */}
                                                <Row className="mb-4">
                                                    <Col>
                                                        <Card className="h-100">
                                                            <Card.Body>
                                                                <Row>
                                                                    <Col md={12}>
                                                                        <h3><span className="item-number">2.</span> Citizen office desk</h3>
                                                                        <hr />
                                                                    </Col>
                                                                </Row>

                                                                <Row className="mb-4">
                                                                    <Col md={6}>
                                                                        <div className="icon-text">
                                                                            <span className="icon" aria-hidden="true">
                                                                                <Image src={icLink} alt="Citizen office desk URL" />
                                                                            </span>
                                                                            <span className="text">
                                                                                Citizen office desk URL
                                                                                <br />
                                                                                <strong>
                                                                                    {safeOfficeHref ? (
                                                                                        <a href={safeOfficeHref} target="_blank" rel="noopener noreferrer">
                                                                                            {formData.officeUrl}
                                                                                        </a>
                                                                                    ) : 'N/A'}
                                                                                </strong>
                                                                            </span>
                                                                        </div>
                                                                    </Col>
                                                                    <Col md={6}>
                                                                        <div className="icon-text">
                                                                            <span className="icon" aria-hidden="true">
                                                                                <Image src={icHome} alt="Address" />
                                                                            </span>
                                                                            <span className="text">
                                                                                Address
                                                                                <br />
                                                                                <strong>
                                                                                    {formData.address || 'N/A'}
                                                                                </strong>
                                                                            </span>
                                                                        </div>
                                                                    </Col>
                                                                </Row>
                                                                <Row>
                                                                    <Col md={6}>
                                                                        <div className="icon-text">
                                                                            <span className="icon" aria-hidden="true">
                                                                                <Image src={icLink} alt="Opening hours" />
                                                                            </span>
                                                                            <span className="text">
                                                                                Opening hours
                                                                                <br />
                                                                                <strong>
                                                                                    {formData.workingHours.length > 0
                                                                                        ? formData.workingHours
                                                                                            .filter(h => h)
                                                                                            .map((h, i) => (
                                                                                                <span key={i}>
                                                                                                    {(h.fromDay || '-')}{h.toDay ? `–${h.toDay}` : ''}:&nbsp;
                                                                                                    {(h.fromTime || '-')}{h.toTime ? `–${h.toTime}` : ''}
                                                                                                    {i < formData.workingHours.length - 1 ? <><br /></> : null}
                                                                                                </span>
                                                                                            ))
                                                                                        : 'N/A'}
                                                                                </strong>
                                                                            </span>
                                                                        </div>
                                                                    </Col>
                                                                    <Col md={6}>
                                                                        <div className="icon-text">
                                                                            <span className="icon" aria-hidden="true">
                                                                                <Image src={icPhone} alt="Phone" />
                                                                            </span>
                                                                            <span className="text">
                                                                                Phone
                                                                                <br />
                                                                                <strong>
                                                                                    {formData.phone || 'N/A'}
                                                                                </strong>
                                                                            </span>
                                                                        </div>
                                                                    </Col>
                                                                </Row>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                </Row>

                                            </>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </main>
        </>
    );
};

export default DiscoveryCityView;
